<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import LightLayout from '@/layouts/LightLayout.vue'
import { Download } from 'lucide-vue-next'

interface ProcessResult {
    errors?: string[]
    rows_errors?: string[]
    valid_amounts?: Record<string, number>
    valid_records?: number
    ticket_amounts?: Record<string, number>
    available_tickets?: number
    will_update_count?: number
    order_id?: number
}

interface Upload {
    id: number
    user: string
    name: string
    processed: boolean
    imported: boolean | null
    process_result: ProcessResult
    created_at: string
}

defineProps<{
    uploads: Upload[]
}>()
</script>

<template>
    <Head :title="$t('Manage orders')" />

    <LightLayout contentClasses="bg-slate-50">
        <main class="container flex flex-col gap-8">
            <Title class="flex items-center gap-2 font-sans font-semibold">
                {{ $t('Imports') }}
            </Title>

            <section class="flex flex-col gap-4">
                <Card v-for="upload in uploads" :key="upload.id" class="bg-white shadow-sm">
                    <CardHeader>
                        <CardTitle class="flex items-center justify-between">
                            <div>
                                <span class="font- bold">{{ $t('File Name') }}:</span> {{ upload.name }}
                            </div>
                            <div class="text-sm text-gray-500">
                                <strong>{{ $t('User') }}:</strong> {{ upload.user }}
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div class="flex flex-col gap-2">
                            <div>
                                <strong>{{ $t('Import date') }}:</strong>
                                {{ upload.created_at }}
                            </div>

                            <div>
                                <strong>{{ $t('Status') }}:</strong>
                                <span
                                    :class="{
                                        'text-green-600': upload.processed && upload.imported,
                                        'text-yellow-600': upload.processed && upload.imported === null,
                                        'text-red-600': !upload.processed,
                                    }"
                                >
                                    {{
                                        !upload.processed ? $t('Not processed') : upload.imported === null ? $t('Partially imported') : $t('Imported')
                                    }}
                                </span>
                            </div>

                            <div v-if="upload.process_result.order_id">
                                <strong>{{ $t('Order ID') }}:</strong> {{ upload.process_result.order_id }}
                            </div>

                            <div v-if="upload.process_result.available_tickets !== undefined">
                                <strong>{{ $t('Available Tickets') }}:</strong> {{ upload.process_result.available_tickets }}
                            </div>

                            <div v-if="upload.process_result.ticket_amounts">
                                <strong>{{ $t('Ticket Amounts') }}:</strong>
                                <ul class="ml-4 list-disc">
                                    <li v-for="(value, key) in upload.process_result.ticket_amounts" :key="key">
                                        {{ $t(key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())) }}: {{ value }}
                                    </li>
                                </ul>
                            </div>

                            <div v-if="upload.process_result.valid_amounts && Object.keys(upload.process_result.valid_amounts).length">
                                <strong>{{ $t('Valid Amounts') }}:</strong>
                                <ul class="ml-4 list-disc">
                                    <li v-for="(value, key) in upload.process_result.valid_amounts" :key="key">
                                        {{ $t(key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())) }}: {{ value }}
                                    </li>
                                </ul>
                            </div>

                            <div v-if="upload.process_result.valid_records !== undefined">
                                <strong>{{ $t('Valid Records') }}:</strong> {{ upload.process_result.valid_records }}
                            </div>

                            <div v-if="upload.process_result.will_update_count !== undefined">
                                <strong>{{ $t('Will Update Count') }}:</strong> {{ upload.process_result.will_update_count }}
                            </div>

                            <div v-if="upload.process_result.errors?.length">
                                <strong class="text-red-600">{{ $t('Errors') }}:</strong>
                                <ul class="ml-4 list-disc text-red-600">
                                    <li v-for="(error, idx) in upload.process_result.errors" :key="idx">{{ error }}</li>
                                </ul>
                            </div>

                            <div v-if="upload.process_result.rows_errors?.length">
                                <strong class="text-orange-600">{{ $t('rows errors') }}:</strong>
                                <ul class="ml-4 list-disc text-orange-600">
                                    <li v-for="(error, idx) in upload.process_result.rows_errors" :key="idx">{{ error }}</li>
                                </ul>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter>
                        <Button variant="outline" as="a" :href="`/admin/uploads/${upload.id}/download`" target="_blank" download class="mb-4">
                            <Download class="mr-2 h-4 w-4" /> {{ $t('Download file') }}
                        </Button>
                    </CardFooter>
                </Card>
            </section>
        </main>
    </LightLayout>
</template>
